import {Injectable} from '@angular/core';
import {FilterModel, MesApiService, Page, PagedData} from 'common-shared';
import {map} from 'rxjs/operators';
import {instanceToInstance, plainToInstance} from 'class-transformer';
import {SpecLineFilterModel} from 'web-shared';
import {marker} from "@jsverse/transloco-keys-manager/marker";
import {IntegratedMasterListColumns, TableColumn} from '../../core/table-column';

export class IntegratedMasterListModel {
  ItemId: string;
  ItemGroupId: string;
  InstructionValidCondition: string;
}

export class IntegratedMasterFilterModel {
  InstructionSpecs: SpecLineFilterModel[] = [];
  OperationSpecs: SpecLineFilterModel[] = [];
  ProcessSelectorSpecs: SpecLineFilterModel[] = [];
  TaskSelectorSpecs: SpecLineFilterModel[] = [];
  ProcessIds: string[] = [];
  InstructionTypes: string[] = [];
  InstructionItemIds: string[] = [];
  InstructionItemGroupIds: string[] = [];
  InstructionResourceIds: string[] = [];
  InstructionResourceTemplateIds: string[] = [];
  InstructionItemIdExists: boolean;
  InstructionItemGroupIdExists: boolean;
  InstructionResourceIdExists: boolean;
  InstructionResourceTemplateIdExists: boolean;
  TargetItemIds: string[] = [];
  TargetItemGroupIds: string[] = [];
  ProcessNumbers: string[] = [];
  ProcessSelectorCodes: string[] = [];
  TaskSelectorCodes: string[] = [];
  Customers: string[] = [];
  InstructionCodes: string[] = [];
  InstructionValidCondition: string;
  ProcessValidCondition: string;
  ProcessSelectorValidCondition: string;
  TaskSelectorValidCondition: string;
  PrevNumbers: string[] = [];
  ValidFrom: Date;
  ValidTo: Date;
  TimeConstraintMethods: string[] = [];
  TargetTypes: string[] = [];
  SelectedColumns: IntegratedMasterListColumns[] = [];

  constructor() {
    this.InstructionSpecs = [new SpecLineFilterModel()];
    this.OperationSpecs = [new SpecLineFilterModel()];
    this.ProcessSelectorSpecs = [new SpecLineFilterModel()];
    this.TaskSelectorSpecs = [new SpecLineFilterModel()];
    this.TargetTypes = ['Item', 'ItemGroup'];
    this.SelectedColumns = [
      IntegratedMasterListColumns.Target,
      IntegratedMasterListColumns.ProcessSelector,
      IntegratedMasterListColumns.ProcessNumber,
      IntegratedMasterListColumns.Process,
      IntegratedMasterListColumns.OperationSpecs,
      IntegratedMasterListColumns.TaskSelector,
      IntegratedMasterListColumns.Code,
      IntegratedMasterListColumns.Type,
      IntegratedMasterListColumns.Item,
      IntegratedMasterListColumns.Production,
      IntegratedMasterListColumns.Yield,
      IntegratedMasterListColumns.InstructionValidCondition,
      IntegratedMasterListColumns.InstructionSpecs];
  }
}

@Injectable({
  providedIn: 'root'
})
export class ImeListService extends MesApiService {

  constructor() {
    super('IntegratedMaster', 'manufacturing');
  }

  getByFilter(filter: FilterModel<IntegratedMasterFilterModel>) {
    let clonedFilter = instanceToInstance(filter);
    clonedFilter.Data.InstructionSpecs = clonedFilter.Data.InstructionSpecs.filter(value => value.PropertyId);
    clonedFilter.Data.OperationSpecs = clonedFilter.Data.OperationSpecs.filter(value => value.PropertyId);
    return this.addGet<FilterModel<IntegratedMasterFilterModel>, PagedData<IntegratedMasterListModel>>(clonedFilter, {action: 'GetByFilter'}).pipe(map(value1 => {
      return new PagedData<IntegratedMasterListModel>(plainToInstance(IntegratedMasterListModel, value1.data.Data), plainToInstance(Page, value1.data.Page));
    }));
  }

  getAllColumns(): TableColumn<IntegratedMasterListColumns>[] {
    return [
      {
        Identifier: IntegratedMasterListColumns.Target,
        Name: marker('ime.manufacturing.ime-list.Target'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.Target],
        Width: 20,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.Customer,
        Name: marker('ime.manufacturing.ime-list.Customer'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.Customer],
        Width: 10,
        IsSelected: false
      },
      {
        Identifier: IntegratedMasterListColumns.ProcessSelector,
        Name: marker('ime.manufacturing.ime-list.Process Selector'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.ProcessSelector],
        Width: 10,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.ProcessSelectorValidCondition,
        Name: marker('ime.manufacturing.ime-list.PS Valid'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.ProcessSelectorValidCondition],
        Width: 10,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.ProcessSelectorSpecs,
        Name: marker('ime.manufacturing.ime-list.PS Specs'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.ProcessSelectorSpecs],
        Width: 15,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.ProcessNumber,
        Name: marker('ime.manufacturing.ime-list.Process Nr'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.ProcessNumber],
        Width: 5,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.Process,
        Name: marker('ime.manufacturing.ime-list.Process'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.Process],
        Width: 10,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.ProcessValidCondition,
        Name: marker('ime.manufacturing.ime-list.Process Valid'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.ProcessValidCondition],
        Width: 10,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.OperationSpecs,
        Name: marker('ime.manufacturing.ime-list.Operation Specs'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.OperationSpecs],
        Width: 15,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.TaskSelector,
        Name: marker('ime.manufacturing.ime-list.Task Selector'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.TaskSelector],
        Width: 10,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.TaskSelectorValidCondition,
        Name: marker('ime.manufacturing.ime-list.TS Valid'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.TaskSelectorValidCondition],
        Width: 10,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.TaskSelectorSpecs,
        Name: marker('ime.manufacturing.ime-list.TS Specs'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.TaskSelectorSpecs],
        Width: 15,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.Code,
        Name: marker('ime.manufacturing.ime-list.Code'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.Code],
        Width: 5,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.Type,
        Name: marker('ime.manufacturing.ime-list.Type'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.Type],
        Width: 10,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.Item,
        Name: marker('ime.manufacturing.ime-list.Item Group | Item | Resource Template | Resource'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.Item],
        Width: 15,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.PrevNumber,
        Name: marker('ime.manufacturing.ime-list.Prev Nr'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.PrevNumber],
        Width: 5,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.Setup,
        Name: marker('ime.manufacturing.ime-list.Setup'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.Setup],
        Width: 8,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.Production,
        Name: marker('ime.manufacturing.ime-list.Production'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.Production],
        Width: 8,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.Teardown,
        Name: marker('ime.manufacturing.ime-list.Teardown'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.Teardown],
        Width: 8,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.InstructionValidCondition,
        Name: marker('ime.manufacturing.ime-list.Instruction Valid Condition'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.InstructionValidCondition],
        Width: 10,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.Valid,
        Name: marker('ime.manufacturing.ime-list.Valid From/To'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.Valid],
        Width: 10,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.Yield,
        Name: marker('ime.manufacturing.ime-list.Yield'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.Yield],
        Width: 5,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.InstructionSpecs,
        Name: marker('ime.manufacturing.ime-list.Instruction Specs'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.InstructionSpecs],
        Width: 15,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.TimeConstraintMethodClv,
        Name: marker('ime.manufacturing.ime-list.TC'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.TimeConstraintMethodClv],
        Width: 5,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.TimeConstraintMin,
        Name: marker('ime.manufacturing.ime-list.TC-Min'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.TimeConstraintMin],
        Width: 5,
        IsSelected: true
      },
      {
        Identifier: IntegratedMasterListColumns.TimeConstraintMax,
        Name: marker('ime.manufacturing.ime-list.TC-Max'),
        Code: IntegratedMasterListColumns[IntegratedMasterListColumns.TimeConstraintMax],
        Width: 5,
        IsSelected: true
      },
    ];
  }
}
